import { defineComponent as _defineComponent } from 'vue'
import { TitleOfferFragment } from '@/features/buybox/graphql/fragments/Offer.fragment';
import { computed, ref } from 'vue';
import GridBuyBox from '@/features/buybox/components/grid/GridBuyBox.vue';
import { MonetizationType, PresentationType, TitleListType } from '@/@types/graphql-types';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { useI18n } from '@/helpers/composables/useI18n';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { SnowplowContext } from '@/helpers/tracking/providers';
import { isTitleInList } from '@/helpers/title-helper';
import { faChevronUp, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { storeToRefs } from 'pinia';
// EXP_LINEAR_TV
import { useExperiment } from '@/libraries/experiment';
// EXP_LINEAR_TV


export default /*#__PURE__*/_defineComponent({
  __name: 'BuyBoxBanner',
  props: {
    offerCount: null,
    offers: null,
    title: null,
    seasonNumber: { default: null },
    additionalContexts: null,
    isAVODActive: { type: Boolean }
  },
  setup(__props: any) {

const props = __props as { offerCount: number, offers: TitleOfferFragment[], title: TitleDetail, seasonNumber: number | null, additionalContexts: SnowplowContext[], isAVODActive?: boolean };

const { t, tc } = useI18n();
const { language, country } = storeToRefs(useLanguageStore());

const showBuyBox = ref(false);



const showDetails = computed(() => {
	if (props.title.__typename !== 'Season') return null;
	return {
		id: props.title.id,
		title: props.title.content.title,
		objectId: props.title.show.objectId,
		objectType: props.title.show.objectType,
	};
});

function toggleBuyBox() {
	showBuyBox.value = !showBuyBox.value;

	TrackingHelper.trackEvent(
		'userinteraction',
		{ action: 'dropdown_clicked', label: 'buybox', value: showBuyBox.value ? 1 : 0 },
		props.additionalContexts
	);
}

function scrollTo(id: string) {
	const element = document.getElementById(id);
	if (element) {
		element.scrollIntoView({ behavior: 'smooth' });
	}
	TrackingHelper.trackEvent('userinteraction', {
		action: 'jumplink_clicked',
		label: id.replaceAll('-', '_'),
	});
}

const showTheatreAndProviderText = computed(() => {
	return ['en', 'fr', 'de', 'es', 'tr', 'pt', 'it'].includes(language.value);
});

const domesticOffers = computed(() => {
	return props.offers.filter(offer => offer.country === country.value);
});

// EXP_LINEAR_TV: linear_free/linear_flatrate offers only exist for variant_1/variant_2
const { activeVariant: linearTVVariant } = useExperiment('EXP_LINEAR_TV', 'local');
const isLinearTVVariantActive = computed(
	() => linearTVVariant.value === 'variant_1' || linearTVVariant.value === 'variant_2'
);

const offers = computed(() => {
	if (isLinearTVVariantActive.value) return domesticOffers.value;
	return domesticOffers.value.filter(
		offer => ![MonetizationType.LinearFree, MonetizationType.LinearFlatrate].includes(offer.monetizationType)
	);
});

const LIVE_TV_MONETIZATION_TYPES = [
	MonetizationType.Fast,
	MonetizationType.LinearFree,
	MonetizationType.LinearFlatrate,
];

const hasLiveTvOffers = computed(() => {
	return offers.value.some(offer => LIVE_TV_MONETIZATION_TYPES.includes(offer.monetizationType));
});

const showLiveTvOfferText = computed(() => isLinearTVVariantActive.value && hasLiveTvOffers.value);
// EXP_LINEAR_TV

const hasOnlyCinemaOffers = computed(() => {
	return offers.value.every(offer => offer.monetizationType === MonetizationType.Cinema);
});

const cinemaOffersCount = computed(() => {
	return offers.value.filter(offer => offer.monetizationType === MonetizationType.Cinema).length;
});

const dvdBluRayOffersCount = computed(() => {
	return props.title.offersDvdBluray.filter(offer =>
		[PresentationType.Dvd, PresentationType.Bluray, PresentationType.Bluray_4K].includes(offer.presentationType)
	).length;
});

const providerCount = computed(() => {
	// START AVOD
	// Only show One Plex offer at a time.
	// + Always filtering out Justwatch-plex Provider as it's only used by partners.
	const AVODTechnicalName = props.isAVODActive
		? ['plex', 'justwatchplexchannel']
		: ['plexplayer', 'justwatchplexchannel'];

	return [...new Map(offers.value.map(offer => [offer.package.packageId, offer])).values()].filter(
		offer =>
			// Only show one plex offer
			!AVODTechnicalName.includes(offer.package.technicalName) &&
			// Filter out DVD/BluRay offers
			![PresentationType.Dvd, PresentationType.Bluray, PresentationType.Bluray_4K].includes(
				offer.presentationType
			) &&
			!LIVE_TV_MONETIZATION_TYPES.includes(offer.monetizationType)
	).length;
});

const allOffersCount = computed(
	() => providerCount.value + dvdBluRayOffersCount.value + (hasLiveTvOffers.value ? 1 : 0)
);

const nonCinemaOffersCount = computed(() => {
	return providerCount.value - cinemaOffersCount.value;
});

const offerCountText = computed(() => {
	if (hasOnlyCinemaOffers.value) return t('WEBAPP_CURRENTLY_IN_THEATERS');

	if (cinemaOffersCount.value > 0 && showTheatreAndProviderText.value) {
		if (nonCinemaOffersCount.value === 1) {
			if (dvdBluRayOffersCount.value > 0) {
				return showLiveTvOfferText.value
					? tc('WEBAPP_IN_THEATERS_AND_ON_STREAMING_SERVICE_DISC_LIVE_TV')
					: tc('WEBAPP_IN_THEATERS_AND_ON_STREAMING_SERVICE_DISC');
			}
			return showLiveTvOfferText.value
				? tc('WEBAPP_IN_THEATERS_AND_ON_STREAMING_SERVICE_LIVE_TV')
				: tc('WEBAPP_IN_THEATERS_AND_ON_STREAMING_SERVICE');
		}

		if (dvdBluRayOffersCount.value > 0 && nonCinemaOffersCount.value === 0) {
			return showLiveTvOfferText.value
				? tc('WEBAPP_IN_THEATERS_DISC_LIVE_TV')
				: tc('WEBAPP_CURRENTLY_IN_THEATERS_DISC');
		}

		// EXP_LINEAR_TV: in theaters + Live TV only, no disc, no other streaming service
		if (nonCinemaOffersCount.value === 0 && showLiveTvOfferText.value) {
			return tc('WEBAPP_IN_THEATERS_LIVE_TV');
		}

		if (dvdBluRayOffersCount.value > 0) {
			return t(
				showLiveTvOfferText.value
					? 'WEBAPP_IN_THEATERS_AND_ON_STREAMING_SERVICES_DISC_LIVE_TV'
					: 'WEBAPP_IN_THEATERS_AND_ON_STREAMING_SERVICES_DISC',
				{ count: nonCinemaOffersCount.value }
			);
		}

		return t(
			showLiveTvOfferText.value
				? 'WEBAPP_IN_THEATERS_AND_ON_STREAMING_SERVICES_LIVE_TV'
				: 'WEBAPP_IN_THEATERS_AND_ON_STREAMING_SERVICES',
			{ count: nonCinemaOffersCount.value }
		);
	}

	if (providerCount.value === 1) {
		if (dvdBluRayOffersCount.value > 0) {
			return showLiveTvOfferText.value
				? tc('WEBAPP_AVAIL_ON_STREAMING_SERVICE_DISC_LIVE_TV')
				: tc('WEBAPP_AVAIL_ON_STREAMING_SERVICE_DISC');
		}
		return showLiveTvOfferText.value
			? tc('WEBAPP_AVAIL_ON_STREAMING_SERVICE_LIVE_TV')
			: tc('WEBAPP_AVAIL_ON_STREAMING_SERVICE');
	}

	if (dvdBluRayOffersCount.value > 0 && providerCount.value > 1) {
		return t(
			showLiveTvOfferText.value
				? 'WEBAPP_AVAIL_ON_STREAMING_SERVICES_DISC_LIVE_TV'
				: 'WEBAPP_AVAIL_ON_STREAMING_SERVICES_DISC',
			{ count: providerCount.value }
		);
	}

	if (dvdBluRayOffersCount.value > 0 && providerCount.value === 0) {
		return showLiveTvOfferText.value
			? tc('WEBAPP_AVAIL_ON_DISC_LIVE_TV')
			: t('WEBAPP_AVAIL_ON_DISC', { count: dvdBluRayOffersCount.value });
	}

	if (providerCount.value === 0 && showLiveTvOfferText.value) {
		return tc('WEBAPP_AVAIL_ON_LIVE_TV');
	}

	return t(
		showLiveTvOfferText.value ? 'WEBAPP_AVAIL_ON_STREAMING_SERVICES_LIVE_TV' : 'WEBAPP_AVAIL_ON_STREAMING_SERVICES',
		{ count: providerCount.value }
	);
});

return { __sfc: true,t, tc, language, country, showBuyBox, props, showDetails, toggleBuyBox, scrollTo, showTheatreAndProviderText, domesticOffers, linearTVVariant, isLinearTVVariantActive, offers, LIVE_TV_MONETIZATION_TYPES, hasLiveTvOffers, showLiveTvOfferText, hasOnlyCinemaOffers, cinemaOffersCount, dvdBluRayOffersCount, providerCount, allOffersCount, nonCinemaOffersCount, offerCountText, GridBuyBox, TitleListType, isTitleInList, faChevronUp, faChevronDown }
}

})